import { useEffect, useState } from "react";
import {
  ApiError,
  listTenants,
  resendCredentialLink,
  setTenantActive,
  type TenantSummary,
} from "../api/client";

/**
 * Screen 71 — the certification bodies, and the activation decision.
 *
 * The prototype draws this as a register of onboarded bodies: name, accreditation, audit
 * counts. Self-registration puts rows in it that have answered nothing but an address
 * (ADR 0013), so the columns are what is true of *every* row — who registered, when, and
 * their standing — rather than what is only true once an application has been submitted.
 *
 * **Two standings are shown, and they are not the same fact.** Activation is whether the
 * body may sign in. Verification is the Cl. 5.2 assessment of the accreditation, which is
 * M1-4 and which nothing on this screen performs. An administrator can activate a body
 * they have not verified, so a single "approved" column would be a lie in both directions.
 */
export function TenantRegister() {
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    listTenants().then(setTenants).catch(report);
  }, []);

  function report(cause: unknown) {
    setError(cause instanceof ApiError ? cause.message : "The register could not be read.");
  }

  const toggle = async (tenant: TenantSummary) => {
    setError(null);
    setPending(tenant.id);
    try {
      const updated = await setTenantActive(tenant.id, !tenant.active);
      // The stored row replaces the local one. Assuming the write landed and flipping the
      // toggle locally would show an activation the server may have refused.
      setTenants((current) =>
        (current ?? []).map((row) => (row.id === updated.id ? updated : row)),
      );
    } catch (cause) {
      report(cause);
    } finally {
      setPending(null);
    }
  };

  /**
   * Send the body its sign-in link again — M9-0.
   *
   * Delivery is best effort with no outbox, so a message that did not arrive leaves a log
   * line and nothing else. This is the recovery, and it has to be the administrator's: a
   * body that cannot sign in cannot ask for a link itself.
   */
  const resend = async (tenant: TenantSummary) => {
    setError(null);
    setSent(null);
    setPending(tenant.id);
    try {
      const issued = await resendCredentialLink(tenant.id);
      setSent(issued.credential_link.issued_to);
      // The stored link state replaces the local one, and only this row moves. Refetching
      // the whole register to learn one timestamp would rewrite every other row too.
      setTenants((current) =>
        (current ?? []).map((row) =>
          row.id === tenant.id ? { ...row, credential_link: issued.credential_link } : row,
        ),
      );
    } catch (cause) {
      report(cause);
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      <div className="eyebrow">Complao admin</div>
      <h1>Certification bodies</h1>
      <p className="sub" style={{ marginBottom: "18px" }}>
        Each body is a tenant and its data is segregated by row-level security. A body
        registers itself and cannot sign in until it is activated here. Activation is not
        verification &mdash; the Cl.&nbsp;5.2 assessment of the accreditation is a separate
        decision.
      </p>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {sent ? (
        <p className="callout green" role="status">
          A sign-in link was sent to <b>{sent}</b>. It is not shown here and it is not
          recoverable &mdash; only its digest is stored, so a body that does not receive it
          needs another one.
        </p>
      ) : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th>Certification body</th>
                <th>Accreditation</th>
                <th>Registered</th>
                <th>Application</th>
                <th>Standing</th>
                <th>Sign-in link</th>
                <th>Sign-in</th>
              </tr>
            </thead>
            <tbody>
              {tenants === null ? (
                <tr>
                  <td colSpan={7}>Reading the register&hellip;</td>
                </tr>
              ) : tenants.length === 0 ? (
                <tr>
                  <td colSpan={7}>No certification body has registered yet.</td>
                </tr>
              ) : (
                tenants.map((tenant) => (
                  <tr key={tenant.id}>
                    <td>
                      <b>{tenant.legal_name ?? tenant.contact_email ?? "—"}</b>
                      {tenant.legal_name && tenant.contact_email ? (
                        <div className="meta">{tenant.contact_email}</div>
                      ) : null}
                    </td>
                    <td className="mono">
                      {tenant.accreditation_body
                        ? `${tenant.accreditation_body} ${tenant.accreditation_number ?? ""}`.trim()
                        : "—"}
                    </td>
                    <td>{formatDate(tenant.registered_at)}</td>
                    <td>
                      {tenant.onboarding_complete ? (
                        <span className="pill p-ok">Submitted</span>
                      ) : (
                        <span className="pill p-wait">Not started</span>
                      )}
                    </td>
                    <td>{<Standing tenant={tenant} />}</td>
                    <td>
                      <CredentialLink
                        tenant={tenant}
                        busy={pending === tenant.id}
                        onResend={() => void resend(tenant)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={tenant.active ? "btn sec sm" : "btn sm"}
                        onClick={() => void toggle(tenant)}
                        disabled={pending === tenant.id}
                      >
                        {tenant.active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * When the body was last sent a sign-in link, and the button that sends another.
 *
 * *Resend* on its own does not say what will happen, so the cell says when the last one
 * went and where before it offers one. The link itself is never here: it exists in one
 * inbox, and that is the whole of what makes holding it a claim to the address.
 *
 * **Offered only while the body may sign in.** A link mailed to a body that is still
 * awaiting activation, or that has been deactivated, would set a password the account
 * cannot then use &mdash; and activation is what sends the first link anyway.
 */
function CredentialLink({
  tenant,
  busy,
  onResend,
}: {
  tenant: TenantSummary;
  busy: boolean;
  onResend: () => void;
}) {
  if (!tenant.active) {
    return (
      <span className="meta">
        {tenant.credential_link ? `Last sent ${formatDate(tenant.credential_link.issued_at)}` : "—"}
      </span>
    );
  }
  return (
    <>
      <div className="meta">
        {tenant.credential_link
          ? `${formatDate(tenant.credential_link.issued_at)} · ${tenant.credential_link.issued_to}`
          : "None sent"}
      </div>
      <button type="button" className="btn sec sm" onClick={onResend} disabled={busy}>
        Resend
      </button>
    </>
  );
}

/**
 * The three `tenant_status` values, said in words rather than as a colour alone.
 *
 * `pending_verification` and `suspended` both mean "cannot sign in" and are deliberately
 * not merged: the first says no decision has been taken, the second says one has.
 */
function Standing({ tenant }: { tenant: TenantSummary }) {
  if (tenant.active) return <span className="pill p-ok">Active</span>;
  // Amber is "waiting on someone"; a deactivation is a decision that was taken.
  return tenant.status === "suspended" ? (
    <span className="pill p-off">Deactivated</span>
  ) : (
    <span className="pill p-wait">Awaiting activation</span>
  );
}

function formatDate(value: string): string {
  const when = new Date(value);
  return Number.isNaN(when.getTime())
    ? "—"
    : when.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
